/* global process */
// POSSIBLE CONNECTIONS in the chat (chat Figma rebuild WP4, frames 19–21 / M09, node
// 40012285:343526, gap B10).
//
// The `connections` answer block (PLAN §2) lists what an item the conversation is about — its
// `subject` ("Best dark chocolate", a research pack) — may belong with, one row per candidate:
//
//   { ref: { type: ongoing|task|pack|thread, id }, module, title, reason, lastUpdateUtc,
//     confidence, band, timelineId?, ongoingId?, ongoingTitle?, relationshipId? }
//
// and every row can be answered three ways: View (open it), Not related (drop it), Connect (put
// the two together in one Ongoing). This module is everything about that which is not drawing:
// the row's reading (glyph, date, chip), what Connect / Undo / Not related call, and the words
// the box's notice strip says while they run. Components/Home/blocks/ConnectionsBlock.vue wires it
// to the store; parts/ConnectionsTableView.vue draws the rows it produces.
//
// WHAT CONNECT MEANS. Busy's one way of putting two things together is an Ongoing holding a
// timeline (`ongoing/linkTimeline` — tasks, packs and threads are all timelines), so a connection
// always names ONE Ongoing and ONE timeline:
//   · a row that IS an Ongoing ("Dad's birthday") takes the subject's timeline;
//   · any other row (a task, a pack, a thread) is the timeline, and the Ongoing is the row's own
//     (`ongoingId`), else the subject's (`subject.ongoingId`, or the subject itself when it is one).
// Where the server only offered the pair as a Suggested edge (`relationshipId`) and the two ids are
// not both on the row, Connect confirms that edge instead — the same verb the in-Ongoing table uses.
// With neither, there is nothing to call, and Connect says so rather than pretending.
//
// Pure apart from the three async calls at the bottom, which take the store and the api in.

import { moduleColor } from '@/lib/moduleColors'
import { moduleIcon, moduleIconKey } from '@/lib/moduleIcons'
import { confidenceBand, connectionDate } from '@/lib/possibleConnections'

export const CONNECTIONS_TEXT = {
  heading: 'Possible connections',
  name: 'Name',
  reason: 'Possible connection',
  lastUpdate: 'Last update',
  confidence: 'Confidence',
  view: 'View',
  notRelated: 'Not related',
  connect: 'Connect',
  connected: 'Connected',
  connecting: 'Connecting',
  undo: 'Undo',
  // The open row's "View" while its briefing is up — the in-Ongoing table's word
  // (lib/possibleConnections TEXT.viewing), for the graph panel's rows (busy-my-web#710).
  viewing: 'Viewing',
}

// The two chips the frame draws (Strong = the product indigo, filled; Possible = the grey plate,
// sampled #EDEEEF / #4B5563), and the middle rung lib/possibleConnections has always had for the
// server's `likely`, kept in that table's colours so a band reads the same in both places.
export const BAND_CHIPS = {
  strong: { key: 'strong', label: 'Strong', class: 'bg-[#3741f3] text-white' },
  likely: { key: 'likely', label: 'Likely', class: 'bg-[#f4f9ff] text-[#3741f3]' },
  possible: { key: 'possible', label: 'Possible', class: 'bg-[#edeeef] text-[#4b5563]' },
}

// What the strip is saying, and the endpoint a "Not related" with no Suggested edge is reported to
// (WP6, busy-intelligent-srv via the BFF). Until that lands it answers 404, which is tolerated.
export const DISMISS_PATH = 'possible-relations/dismiss'
// How long the landed / dropped notices stay up — long enough to reach Undo.
export const UNDO_WINDOW_MS = 6000

const str = (v) => (v === null || v === undefined ? '' : String(v).trim())

/** The ROW's stable key in its block: its ref, else its position. What row state is filed under. */
export function rowKey(row, index = 0) {
  const type = str(row?.ref?.type)
  const id = str(row?.ref?.id)
  return type && id ? `${type}:${id}` : `row-${index}`
}

/**
 * The band a row's chip shows: the server's own `band` where it sent one, else the score read
 * through the in-Ongoing table's thresholds (lib/possibleConnections `confidenceBand`), so a row
 * never claims "Strong" without a reason.
 */
export function bandChip(row) {
  const band = str(row?.band).toLowerCase()
  if (BAND_CHIPS[band]) return BAND_CHIPS[band]
  return BAND_CHIPS[confidenceBand(row?.confidence).key]
}

/** Which module's glyph a row wears — its `module`, else its ref type, else the research globe. */
export function rowModule(row) {
  const raw = str(row?.module).toLowerCase() || str(row?.ref?.type).toLowerCase()
  if (raw === 'thread') return 'email'
  if (raw === 'pack' || raw === 'ongoing') return 'research'
  return moduleIconKey(raw) || 'research'
}

/** The state a row is in: '' (open), 'connecting', 'connected' or 'hidden'. */
export function rowState(block, key) {
  const states = block?.rowStates
  return (states && typeof states === 'object' && str(states[key])) || ''
}

/** The block's `rowStates` with one row moved — a NEW object, so the patch is a real change. */
export function withRowState(block, key, state) {
  const next = { ...((block?.rowStates && typeof block.rowStates === 'object') ? block.rowStates : {}) }
  if (state) next[key] = state
  else delete next[key]
  return next
}

/**
 * The rows as the table draws them, in the server's order, minus the ones answered "Not related".
 * Everything a cell shows is here, so the view does no reading of its own.
 */
export function connectionsView(block, now = new Date()) {
  const rows = Array.isArray(block?.rows) ? block.rows : []
  return rows
    .map((row, i) => {
      if (!row || typeof row !== 'object') return null
      const key = rowKey(row, i)
      const module = rowModule(row)
      return {
        key,
        row,
        title: str(row.title),
        module,
        icon: moduleIcon(module),
        color: moduleColor(module),
        reason: str(row.reason),
        date: connectionDate(row.lastUpdateUtc, now),
        chip: bandChip(row),
        state: rowState(block, key),
      }
    })
    .filter((r) => r && r.state !== 'hidden')
}

/**
 * The ONE Ongoing and ONE timeline a Connect joins (see the header), and the two names the notice
 * says. Null when the block gives nothing to call.
 */
export function connectTarget(block, row) {
  if (!row) return null
  const subject = block?.subject || {}
  const isOngoing = str(row.ref?.type) === 'ongoing'
  const subjectOngoing = str(subject.type) === 'ongoing'
  const ongoingId = isOngoing
    ? str(row.ref.id)
    : str(row.ongoingId) || str(subject.ongoingId) || (subjectOngoing ? str(subject.id) : '')
  const timelineId = isOngoing ? str(subject.timelineId) : str(row.timelineId)
  const relationshipId = str(row.relationshipId)
  const via = ongoingId && timelineId ? 'link' : (relationshipId ? 'suggested' : '')
  if (!via) return null
  const ongoingTitle = isOngoing
    ? str(row.title)
    : str(row.ongoingTitle) || str(subject.ongoingTitle) || (subjectOngoing ? str(subject.title) : '')
  return {
    via,
    ongoingId,
    timelineId,
    relationshipId,
    itemTitle: isOngoing ? str(subject.title) : str(row.title),
    ongoingTitle,
  }
}

/** "Connecting to Dad's birthday ongoing..." (frame 20). */
export function connectingText(target) {
  const where = str(target?.ongoingTitle)
  return where ? `${CONNECTIONS_TEXT.connecting} to ${where} ongoing...` : `${CONNECTIONS_TEXT.connecting}...`
}

/** "Best dark chocolate connected to Dad's birthday ongoing." (frame 21). */
export function connectedText(target) {
  const what = str(target?.itemTitle) || 'It'
  const where = str(target?.ongoingTitle)
  return where ? `${what} connected to ${where} ongoing.` : `${what} connected.`
}

export function connectFailedText(row) {
  const what = str(row?.title)
  return what ? `Couldn't connect ${what}. Try again.` : "Couldn't connect. Try again."
}

export function notRelatedText(row) {
  const what = str(row?.title)
  return what ? `${what} marked as not related.` : 'Marked as not related.'
}

/**
 * What VIEW opens for a row — a description, so the component only has to carry it out:
 *   · an Ongoing → its page;
 *   · a task / note / event → the box's editor (`smartbox-edit-entity`, every opener's event);
 *   · a pack → its Ongoing focused on it, when the row says which Ongoing;
 *   · a thread → the reader (lib/threadReader `openThreadInReader`).
 * Null when there is nothing to open.
 */
const MENU_OF = { task: 'tasks', note: 'notes', event: 'events', contact: 'contacts' }

export function openTarget(row) {
  const type = str(row?.ref?.type)
  const id = str(row?.ref?.id)
  if (!id) return null
  if (type === 'ongoing') return { kind: 'route', path: `/ongoing/${encodeURIComponent(id)}` }
  if (type === 'thread') return { kind: 'thread', row: { timelineId: str(row.timelineId) || id, subject: str(row.title) } }
  if (type === 'pack') {
    const home = str(row.ongoingId)
    return home ? { kind: 'route', path: `/ongoing/${encodeURIComponent(home)}?pack=${encodeURIComponent(id)}` } : null
  }
  const menu = MENU_OF[type] || MENU_OF[rowModule(row)]
  return menu ? { kind: 'entity', detail: { menu, id, title: str(row.title) } } : null
}

/** The Ongoing a landed connection's View opens. */
export function connectedPath(target) {
  const id = str(target?.ongoingId)
  return id ? `/ongoing/${encodeURIComponent(id)}` : ''
}

// ── The calls ────────────────────────────────────────────────────────────────────────────────

// ── The fixture demo (DEV ONLY) ──────────────────────────────────────────────────────────────
// tests/fixtures/chat/connections.json carries ids prefixed `fixture-` (lib/chatFixture.js). In a
// dev build those are answered here, after a beat, rather than sent to a server that has never heard
// of them — so the fixture walks through Connecting → Connected → Undo the way frames 20/21 do.
// Production builds fold `process.env.NODE_ENV` and never take this branch.
export const FIXTURE_DELAY_MS = 900
export function isFixtureId(...ids) {
  if (process.env.NODE_ENV === 'production') return false
  return ids.some((id) => /^fixture-/.test(str(id)))
}
const fixtureBeat = () => new Promise((resolve) => setTimeout(() => resolve(true), FIXTURE_DELAY_MS))

/** Connect. Answers true when it landed. */
export async function connect(store, target) {
  if (!target) return false
  if (isFixtureId(target.ongoingId, target.timelineId, target.relationshipId)) return fixtureBeat()
  try {
    const ok = target.via === 'link'
      ? await store.dispatch('ongoing/linkTimeline', { id: target.ongoingId, timelineId: target.timelineId })
      : await store.dispatch('ongoing/addSuggestedThread', target.relationshipId)
    return ok !== false
  } catch {
    return false
  }
}

/**
 * Undo a Connect. A linked timeline is unlinked (`ongoing/unlinkTimeline` →
 * `DELETE v1/ongoing/{id}/timelines/{timelineId}`, the BFF's UnlinkTimeline); a confirmed Suggested edge is
 * rejected, the in-Ongoing table's own undo (and its known cost: the pair is not offered again).
 */
export async function undoConnect(store, api, target) {
  if (!target) return false
  if (isFixtureId(target.ongoingId, target.timelineId, target.relationshipId)) return true
  try {
    if (target.via === 'link') {
      return (await store.dispatch('ongoing/unlinkTimeline', { id: target.ongoingId, timelineId: target.timelineId })) !== false
    }
    return (await store.dispatch('ongoing/rejectSuggestedThread', target.relationshipId)) !== false
  } catch {
    return false
  }
}

/**
 * Not related. A Suggested edge is rejected on the edge; anything else is reported to the dismiss
 * endpoint so it is not offered again. A failure (the endpoint not shipped yet answers 404) is
 * swallowed: the row is already gone from this answer, which is what the person asked for.
 */
export async function dismiss(store, api, block, row) {
  const relationshipId = str(row?.relationshipId)
  if (isFixtureId(relationshipId, row?.ref?.id)) return true
  try {
    if (relationshipId) return (await store.dispatch('ongoing/rejectSuggestedThread', relationshipId)) !== false
    await api.insertWithPath('intelligent', DISMISS_PATH, { subjectId: str(block?.subject?.id), refId: str(row?.ref?.id) })
    return true
  } catch {
    return false
  }
}
